<template>
  <div class="flex">
    <div class="flex-shrink-0" :style="{ paddingRight: '37.5rem' }">
      <div
        class="sticky left-0 z-30 flex items-center justify-between px-6 h-14 top-24 bg-slate-300"
        :style="{ width: 'calc(100vw - 37.5rem - 0.5rem)' }"
      >
        <div>
          <BlTextBody2
            :text="`Showing ${bookingTimeslots.length} appointment schedule(s)`"
          />
        </div>
        <div class="flex items-center gap-2">
          <div>
            <BlSelectBox
              v-model="timeslotView"
              :options="timeslotViewOptions"
              dense
              @change="$emit('set-timeslot-view', $event)"
            />
          </div>
          <div
            class="h-7.5 w-72 flex items-center justify-center bg-pure-white hover:bg-slate-400 rounded border border-slate-400 transition-all duration-300 cursor-pointer"
          >
            <BlTextBody2 :text="masterDate" />
            <input
              type="date"
              class="absolute opacity-0 bl-input-full-indicator w-72 "
              v-model="dateInput"
              @change="onDateInput($event.target.value)"
              :min="minimumDate"
              :max="maximumDate"
            />
          </div>
          <BlInputButton
            type="outlined"
            dense
            class="rounded-full"
            icon="chevron-left"
            :disabled="isDateBackDisabled"
            @clicked="onNextDate(-7)"
          />
          <BlInputButton
            type="outlined"
            dense
            class="rounded-full"
            icon="chevron-right"
            :disabled="isDateNextDisabled"
            @clicked="onNextDate(7)"
          />
        </div>
      </div>
      <div class="sticky z-20 flex h-12 top-38 bg-slate-200">
        <div
          class="sticky left-0 z-10 flex-shrink-0 w-20 h-12 border-b bg-slate-200 border-slate-400"
        ></div>
        <div
          v-for="(n, idx) in headers"
          :key="idx"
          class="flex items-center justify-center flex-shrink-0 h-12 px-2 py-1 border-b border-slate-400"
          :style="{ width: `calc(${slotWidth})` }"
        >
          <div v-if="n.highlight" class="flex items-center gap-1 h-7">
            <BlTextBody2 :text="n.label.split(' ')[0]" />
            <div
              class="flex items-center justify-center rounded-full h-7 w-7 bg-light-purple-400"
            >
              <BlTextBody2
                :text="n.label.split(' ').pop()"
                class="text-pure-white"
              />
            </div>
          </div>
          <div v-else class="px-4 truncate h-7" :title="n.label">
            <BlTextBody2 :text="n.label" class="truncate" />
          </div>
        </div>
      </div>
      <div class="relative flex flex-col bg-slate-200">
        <div
          v-for="time in clockRound"
          :key="time"
          class="flex"
          :id="`recurring_time_view_${time}`"
        >
          <div
            class="sticky left-0 z-10 flex justify-end flex-shrink-0 w-20 px-3 py-2 transition-all duration-300 border-b border-r bg-slate-200 border-slate-400"
            :style="{ height: `${slotHeight * 2}rem` }"
          >
            <BlTextBody2 :text="time" />
          </div>
          <div
            v-for="(n, idx) in headers"
            :key="idx"
            class="flex flex-col items-center justify-center flex-shrink-0 gap-2 transition-all duration-300 border-b border-r border-slate-400"
            :class="[
              onCheckUsedBlock(n.value) ? 'bg-slate-200' : 'bg-slate-300'
            ]"
            :style="{
              height: `${slotHeight * 2}rem`,
              width: `calc(${slotWidth})`
            }"
          ></div>
        </div>
      </div>
    </div>

    <div
      class="fixed z-50 flex-shrink-0 overflow-y-auto border top-24 right-2 bg-pure-white border-slate-400 scrollbar-lg"
      :style="{
        height: 'calc(100vh - 6rem - 5rem - 0.5rem)',
        width: '37.5rem'
      }"
    >
      <div
        class="sticky top-0 z-10 flex flex-col gap-3 px-6 py-4 border-b bg-pure-white border-slate-400"
      >
        <BlTextSubtitle1 text="Review and confirm appointments" />
        <div class="flex items-center h-16 gap-4">
          <div
            class="flex flex-col items-center justify-between w-full h-16 gap-1"
          >
            <BlTextTitle3
              :text="bookingTimeslots.length"
              class="text-slate-800"
            />
            <BlTextBody2 text="Total appointments" />
          </div>
          <div class="h-16 w-0.5 flex-shrink-0 bg-slate-300"></div>
          <div
            class="flex flex-col items-center justify-between w-full h-16 gap-1"
          >
            <BlTextTitle3
              :text="totalConflictTimeslots"
              :class="[hasConflict ? 'text-tomato-700' : 'text-slate-500']"
            />
            <BlTextBody2 text="Conflicting appointments" />
          </div>
        </div>
      </div>
      <div class="flex flex-col gap-3 px-6 py-4">
        <div
          class="flex flex-col gap-2 px-4 py-4 rounded"
          :class="[minusCredit ? 'bg-tomato-100' : 'bg-cornflower-200']"
        >
          <div
            class="flex items-center justify-between cursor-pointer"
            @click="calculationOpened = !calculationOpened"
          >
            <div class="flex items-center gap-2">
              <BlIcon
                icon="piggy"
                :color="minusCredit ? 'tomato-7' : 'cornflower-8'"
                class="w-6 h-6"
              />
              <BlTextSubtitle3
                text="Credits calculation"
                :class="[
                  minusCredit ? 'text-tomato-700' : 'text-cornflower-800'
                ]"
              />
            </div>
            <BlIcon
              icon="chevron-up"
              :color="minusCredit ? 'tomato-7' : 'cornflower-8'"
              class="w-6 transition-all duration-300"
              :style="{ rotate: calculationOpened ? '0deg' : '180deg' }"
              clickable
            />
          </div>
          <div v-if="calculationOpened" class="flex flex-col gap-2">
            <div
              v-for="used in usedPackageItemServices"
              :key="used.id"
              class="flex flex-col gap-1"
            >
              <BlTextSubtitle3
                :text="used.package_item_name"
                :class="[
                  used.projected_remaining_credits < 0
                    ? 'text-tomato-700'
                    : 'text-cornflower-800'
                ]"
              />
              <div class="flex items-center justify-between gap-2">
                <BlTextBody2
                  :text="
                    `Credits used for ${used.total_appointments} appointment(s)`
                  "
                  :class="[
                    used.projected_remaining_credits < 0
                      ? 'text-tomato-700'
                      : 'text-cornflower-800'
                  ]"
                />
                <div
                  class="rounded h-5.5 w-11 flex items-center justify-center"
                  :class="[
                    used.projected_remaining_credits < 0
                      ? 'bg-tomato-700'
                      : 'bg-cornflower-800'
                  ]"
                >
                  <BlTextSubtitle3
                    :text="used.total_used_credits"
                    :class="[
                      used.projected_remaining_credits < 0
                        ? 'text-tomato-100'
                        : 'text-cornflower-200'
                    ]"
                  />
                </div>
              </div>
              <div class="flex items-center justify-between gap-2">
                <BlTextBody2
                  :text="
                    `Projected remaining credits after ${used.total_appointments} session(s) is completed`
                  "
                  :class="[
                    used.projected_remaining_credits < 0
                      ? 'text-tomato-700'
                      : 'text-cornflower-800'
                  ]"
                />
                <div
                  class="rounded h-5.5 w-11 flex items-center justify-center"
                  :class="[
                    used.projected_remaining_credits < 0
                      ? 'bg-tomato-700'
                      : 'bg-cornflower-800'
                  ]"
                >
                  <BlTextSubtitle3
                    :text="used.projected_remaining_credits"
                    :class="[
                      used.projected_remaining_credits < 0
                        ? 'text-tomato-100'
                        : 'text-cornflower-200'
                    ]"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="minusCredit && !hasConflict">
          <BlTextBody2
            text="The projected remaining credits are negative due to exceeding the appointment limit. Please adjust the duration of each appointment to ensure the projected remaining credits reach zero, or delete some of the appointments."
          />
        </div>
        <div v-if="!minusCredit && hasConflict">
          <BlTextBody2
            text="The selected therapist is unavailable on one or more of the scheduled dates. Please delete the affected appointments to move forward."
          />
        </div>
        <div v-if="minusCredit && hasConflict">
          <BlTextBody2
            text="Some appointments conflict with therapist availability, and the projected remaining credits are negative due to exceeding the appointment limit. Please delete the conflicting appointments and adjust the number of appointments to proceed."
          />
        </div>
        <div>
          <ItemTimeslot
            v-for="timeslot in sortedTimeslots"
            :key="timeslot.id"
            :timeslot="timeslot"
            :deleteDisabled="timeslot.status === 'booking_in_progress'"
            @delete="$emit('delete-timeslot', $event)"
          />
        </div>
      </div>
    </div>

    <SlotRecurring
      v-for="(slot, idx) in existingSlots"
      :key="`${idx}_${slot.id}`"
      :isCompact="timeslotView === 'compact'"
      :period="slot.period"
      :therapist="slot.user"
      :room="slot.room"
      :position="slot.position"
      :assets="slot.assets"
      :error="slot.is_conflicted"
    />
    <SlotRecurring
      v-for="(slot, idx) in existingAppointments"
      :key="`${idx}_${slot.id}`"
      :isCompact="timeslotView === 'compact'"
      :period="slot.period"
      :therapist="slot.user"
      :room="slot.room"
      :position="slot.position"
      :assets="slot.assets"
      :style="{ zIndex: 1 }"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import moment from "moment";
import { CLOCK_ROUND } from "@/plugins/assets.js";

import SlotRecurring from "./SlotRecurring.vue";
import ItemTimeslot from "./ItemTimeslot.vue";

import BlTextTitle4 from "../../BlTextTitle4.vue";
import BlTextTitle3 from "../../BlTextTitle3.vue";
import BlTextSubtitle1 from "../../BlTextSubtitle1.vue";
import BlTextSubtitle2 from "../../BlTextSubtitle2.vue";
import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextSubtitle4 from "../../BlTextSubtitle4.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlBreadcrumbs from "../../BlBreadcrumbs.vue";
import BlIcon from "../../BlIcon.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlInputToggle from "../../BlInputToggle.vue";
import BlInputText from "../../BlInputText.vue";
import BlInputRadio from "../../BlInputRadio.vue";
import BlSelectBox from "../../BlSelectBox.vue";

export default {
  name: "ReviewAppointmentRecurring",
  components: {
    SlotRecurring,
    ItemTimeslot,
    BlTextTitle4,
    BlTextTitle3,
    BlTextSubtitle1,
    BlTextSubtitle2,
    BlTextSubtitle3,
    BlTextSubtitle4,
    BlTextBody2,
    BlBreadcrumbs,
    BlIcon,
    BlInputButton,
    BlInputToggle,
    BlInputText,
    BlInputRadio,
    BlSelectBox
  },
  props: {
    timeslotView: { type: String },
    timeslotViewOptions: { type: Array },
    // $emit('set-timeslot-view')
    client: { type: Object },
    clientAppointments: { type: Array, default: () => [] }
    // $emit('delete-timeslot')
    // $emit('set-client-appointments')
  },
  data() {
    return {
      dateInput: "",
      calculationOpened: true,
      clockRound: CLOCK_ROUND
    };
  },
  computed: {
    ...mapGetters([
      "currentUser",
      "currentCenter",
      "bookingAppointment",
      "bookingTimeslots"
    ]),
    enableBranch() {
      return this.currentCenter.enable_branch;
    },
    masterDate() {
      const date = this.dateInput;
      const start = moment(date).format("ddd, DD MMM YYYY");
      const end = moment(date)
        .add(6, "days")
        .format("ddd, DD MMM YYYY");
      return `${start} — ${end}`;
    },
    slotHeight() {
      // retun value in rem, 1 slot is 30 minutes
      if (this.timeslotView === "compact") return 6.25 / 2;
      return 15 / 2;
    },
    slotWidth() {
      return `(100vw - 18.75rem - 5rem - 0.5rem) / ${this.headers.length}`;
    },
    headers() {
      const date = this.dateInput;
      const startDate = moment(date);
      const endDate = moment(date).add(6, "days");

      let current = moment(startDate);

      const dates = [];

      while (current.isSameOrBefore(endDate)) {
        const isSame =
          current.format("YYYY-MM-DD") === moment().format("YYYY-MM-DD");
        dates.push({
          highlight: isSame,
          value: current.format("YYYY-MM-DD"),
          label: current.format("ddd DD")
        });
        current.add(1, "days");
      }

      return dates;
    },
    minimumDate() {
      if (!this.bookingTimeslots.length) return "";
      return this.bookingTimeslots[0].date;
    },
    isDateBackDisabled() {
      const date = moment(this.dateInput).add(-1, "days");
      const limit = moment(this.minimumDate);
      return moment(date).isBefore(limit);
    },
    maximumDate() {
      if (!this.bookingTimeslots.length) return "";
      return this.bookingTimeslots[this.bookingTimeslots.length - 1].date;
    },
    isDateNextDisabled() {
      const date = moment(this.dateInput).add(7, "days");
      const limit = moment(this.maximumDate);
      return moment(date).isAfter(limit);
    },
    //
    sortedTimeslots() {
      return this.bookingTimeslots;
      // const arr = [...this.bookingTimeslots];
      // return arr.sort((a, b) => {
      //   const datetimeA = new Date(`${a.date}T${a.start_time_string}:00`);
      //   const datetimeB = new Date(`${b.date}T${b.start_time_string}:00`);
      //   return datetimeA - datetimeB;
      // });
    },
    existingSlots() {
      const arr = [...this.bookingTimeslots];
      return arr
        .filter(i => {
          const start = moment(this.dateInput).add(-1, "days");
          const end = moment(this.dateInput).add(7, "days");
          const appDate = moment(i.date);
          return appDate.isBetween(start, end);
        })
        .map(i => {
          const date = i.date;
          const start = i.start_time_string;
          const end = i.end_time_string;
          const count = this.getSlotCount(start, end);
          const { top, left } = this.getSlotPosition(date, start);

          return {
            ...i,
            period: { start_time_string: start, end_time_string: end },
            position: { top, left },
            assets: {
              height: `calc((${count * this.slotHeight}rem) - 2px)`,
              width: `calc((${this.slotWidth}) - 2px)`,
              background_color: i.secondary_color,
              border_color: i.primary_color,
              color: i.primary_color
            }
          };
        });
    },
    existingAppointments() {
      const arr = [...this.clientAppointments];
      return arr
        .filter(i => {
          const start = moment(this.dateInput).add(-1, "days");
          const end = moment(this.dateInput).add(7, "days");
          const appDate = moment(i.date);
          return appDate.isBetween(start, end);
        })
        .map(i => {
          const date = i.date;
          const start = i.start_time_string;
          const end = i.end_time_string;
          const count = this.getSlotCount(start, end);
          const { top, left } = this.getSlotPosition(date, start);

          return {
            ...i,
            period: { start_time_string: start, end_time_string: end },
            position: { top, left },
            assets: {
              height: `calc((${count * this.slotHeight}rem) - 2px)`,
              width: `calc((${this.slotWidth}) - 2px)`,
              background_color: "#F2F4F7",
              border_color: "#F2F4F7",
              color: "#667085"
            }
          };
        });
    },
    //
    usedPackageItemServices() {
      if (!this.bookingAppointment) return [];
      const arr = [];
      const base =
        [...this.bookingAppointment.used_credit_transaction_services] || [];

      for (let idx = 0; idx < base.length; idx++) {
        const arrIdx = arr.findIndex(
          i => i.package_item_id === base[idx].package_item_id
        );
        if (arrIdx > -1) {
          arr[arrIdx].session_credit += base[idx].session_credit;
          arr[arrIdx].remaining_session_credit +=
            base[idx].remaining_session_credit;
          arr[arrIdx].total_used_credits += base[idx].total_used_credits;
          arr[arrIdx].total_appointments += base[idx].total_appointments;
          arr[arrIdx].projected_remaining_credits +=
            base[idx].projected_remaining_credits;
        } else {
          arr.push(base[idx]);
        }
      }
      return arr;
    },
    totalConflictTimeslots() {
      let count = this.bookingTimeslots.reduce((sum, i) => {
        const conflict = i.is_conflicted ? 1 : 0;
        return sum + conflict;
      }, 0);
      return count;
    },
    hasConflict() {
      return this.totalConflictTimeslots > 0;
    },
    minusCredit() {
      if (!this.bookingAppointment) return true;
      const uses = this.usedPackageItemServices;
      if (!uses.length) return true;

      let minus = false;
      uses.forEach(i => {
        if (i.projected_remaining_credits < 0) minus = true;
      });
      return minus;
    }
  },
  watch: {},
  mounted() {
    if (this.bookingTimeslots.length) {
      const start = this.bookingTimeslots[0].date;
      const end = this.bookingTimeslots[this.bookingTimeslots.length - 1].date;
      this.dateInput = moment(start)
        .startOf("isoWeek")
        .format("YYYY-MM-DD");
      this.fetchAppointments(start, end);
    }

    setTimeout(() => {
      if (!this.bookingTimeslots.length) {
        this.onScrollToTime(`07:00`);
        return;
      }

      const startTime = this.bookingTimeslots[0].start_time_string || "";

      let time = Number(startTime.split(":")[0]);
      if (time < 10) time = `0${time}`;
      this.onScrollToTime(`${time}:00`);
    }, 1000);
  },
  methods: {
    ...mapActions(["actionGet"]),
    onScrollToTime(time) {
      const id = `recurring_time_view_${time}`;
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "center"
        });
      }
    },
    onCheckUsedBlock(date) {
      const start = moment(this.minimumDate).add(-1, "days");
      const end = moment(this.maximumDate).add(1, "days");
      const current = moment(date);
      return current.isBetween(start, end);
    },
    getSlotPosition(date, start) {
      let top = "calc(12.5rem";
      let left = "calc(5rem";

      const indexOfTop = this.clockRound.findIndex(i => {
        const dt = i.split(":")[0];
        return dt === start.split(":")[0];
      });
      top += ` + ${indexOfTop * (this.slotHeight * 2)}rem`;
      if (start.split(":")[1] === "30") {
        top += ` + ${this.slotHeight}rem`;
      }
      top += " + 0.5px)";

      const indexOfLeft = this.headers.findIndex(i => i.value === date);
      if (indexOfLeft > -1) {
        const loop = indexOfLeft;
        for (let index = 0; index < loop; index++) {
          left += ` + ${this.slotWidth}`;
        }
      }
      left += " + 0.5px)";
      return { top, left };
    },
    getSlotCount(start, end) {
      const s = start.split(":").map(i => Number(i));
      const e = end.split(":").map(i => Number(i));
      let slots = 0;
      slots += (e[0] - s[0]) * 2;
      if (s[1] === 30) slots -= 1;
      if (e[1] === 30) slots += 1;
      return slots;
    },
    onDateInput(val) {
      const startOf = moment(val)
        .startOf("isoWeek")
        .format("YYYY-MM-DD");
      this.dateInput = startOf;
    },
    onNextDate(n) {
      const date = moment(this.dateInput)
        .add(n, "days")
        .format("YYYY-MM-DD");
      this.dateInput = date;
    },
    async fetchAppointments(start, end) {
      const params = {
        url: `/api/v1/appointments?client_id=${this.client.id}&status=scheduled,in_progress,completed`
      };
      params.url += `&start_date=${start}&end_date=${end}`;

      const { success, data } = await this.actionGet(params);
      if (!success) return;

      this.$emit("set-client-appointments", data.appointments);
    }
  }
};
</script>

<style scoped lang="scss">
.bl-input-full-indicator {
  &::-webkit-calendar-picker-indicator {
    position: absolute;
    opacity: 0;
    width: auto;
    height: auto;
    cursor: pointer;
    bottom: 0;
    left: 0;
    right: 0;
    top: 0;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/appointments/overlay/ReviewAppointmentRecurring.vue